/**
 * GrammarNoteCard — contextual grammar micro-note shown once per lesson.
 *
 * Appears as a full-screen overlay between two exercise cards when a word the
 * user just answered triggers a known grammar pattern (e.g. "went" → irregular
 * past tense). The user can dismiss it or reveal the answer to the check question.
 */
import { useEffect, useRef, useState } from 'react'
import type { GrammarNote } from '../../types'
import { useLocale } from '../../i18n/context'
import CEFRBadge from '../shared/CEFRBadge'

interface Props {
  note: GrammarNote
  onContinue: () => void
}

export default function GrammarNoteCard({ note, onContinue }: Props) {
  const { tr } = useLocale()
  const gn = tr.grammarNote

  const [answerRevealed, setAnswerRevealed] = useState(false)
  const continueRef = useRef<HTMLButtonElement>(null)

  // Inject fade-in animation once
  useEffect(() => {
    if (document.getElementById('grammar-note-kf')) return
    const s = document.createElement('style')
    s.id = 'grammar-note-kf'
    s.textContent = `
      @keyframes grammarNoteSlideUp {
        from { opacity: 0; transform: translateY(20px); }
        to   { opacity: 1; transform: translateY(0); }
      }
    `
    document.head.appendChild(s)
  }, [])

  // Keyboard: Enter/Space → continue, Escape → continue
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') {
        e.preventDefault()
        onContinue()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onContinue])

  // Auto-focus the continue button for keyboard users
  useEffect(() => {
    continueRef.current?.focus()
  }, [])

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 85,
        background: 'var(--bg-page)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 16px',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 540,
          animation: 'grammarNoteSlideUp 0.3s ease-out both',
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
        }}
      >
        {/* Header pill */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--accent)',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              padding: '5px 14px',
              borderRadius: 50,
              background: 'var(--accent-soft)',
            }}
          >
            {/* Book icon */}
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
            </svg>
            {gn.quickNote}
          </span>
        </div>

        {/* Card */}
        <div
          style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            borderRadius: 16,
            padding: '28px 24px',
            boxShadow: 'var(--shadow-flashcard)',
            position: 'relative',
          }}
        >
          {/* CEFR badge (top-right corner) */}
          {note.cefr_level && (
            <div style={{ position: 'absolute', top: 16, right: 16 }}>
              <CEFRBadge level={note.cefr_level} size="sm" />
            </div>
          )}

          {/* Title */}
          <h2
            style={{
              margin: '0 0 14px',
              fontSize: 18,
              fontWeight: 700,
              color: 'var(--text-primary)',
              lineHeight: 1.3,
              paddingRight: note.cefr_level ? 48 : 0,
            }}
          >
            {note.title}
          </h2>

          {/* Note body */}
          <p
            style={{
              margin: '0 0 0',
              fontSize: 15,
              color: 'var(--text-primary)',
              lineHeight: 1.65,
              fontFamily: 'var(--font-content)',
            }}
          >
            {note.note_text}
          </p>

          {/* Check question */}
          {note.check_question && (
            <div
              style={{
                marginTop: 20,
                padding: '14px 16px',
                background: 'var(--bg-page)',
                borderRadius: 10,
                border: '1px solid var(--border)',
              }}
            >
              <p
                style={{
                  margin: '0 0 10px',
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--text-secondary)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                {gn.quickCheck}
              </p>
              <p
                style={{
                  margin: '0 0 10px',
                  fontSize: 15,
                  color: 'var(--text-primary)',
                  lineHeight: 1.5,
                  fontFamily: 'var(--font-content)',
                }}
              >
                {note.check_question}
              </p>

              {note.check_answer && !answerRevealed && (
                <button
                  onClick={() => setAnswerRevealed(true)}
                  style={{
                    background: 'none',
                    border: '1px solid var(--border)',
                    borderRadius: 50,
                    padding: '7px 16px',
                    fontSize: 13,
                    color: 'var(--text-secondary)',
                    cursor: 'pointer',
                    minHeight: 36,
                  }}
                >
                  {gn.showAnswer}
                </button>
              )}

              {note.check_answer && answerRevealed && (
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '7px 14px',
                    background: 'var(--accent-soft)',
                    borderRadius: 50,
                    fontSize: 14,
                    fontWeight: 600,
                    color: 'var(--accent)',
                    fontFamily: 'var(--font-content)',
                  }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  {note.check_answer}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Continue button */}
        <button
          ref={continueRef}
          onClick={onContinue}
          style={{
            padding: '14px 40px',
            borderRadius: 50,
            border: 'none',
            background: 'var(--accent-fill)',
            color: 'var(--text-on-accent)',
            fontSize: 15,
            fontWeight: 600,
            cursor: 'pointer',
            minHeight: 44,
            alignSelf: 'center',
            minWidth: 200,
            transition: 'opacity 0.15s',
          }}
          onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.opacity = '0.88')}
          onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.opacity = '1')}
        >
          {gn.continueArrow}
        </button>

        {/* Dismiss link */}
        <button
          onClick={onContinue}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--text-secondary)',
            fontSize: 13,
            alignSelf: 'center',
            padding: '8px',
            minHeight: 36,
          }}
        >
          {gn.dismiss}
        </button>
      </div>
    </div>
  )
}
